export const PAGE_CONTENT_MAX_WIDTH = 980;

// Umbral "desktop" para features que solo viven en la versión web de
// escritorio (ver Hilos.js y BottomToolbar.js: el botón central de Hilos vs
// QRScanner). Se comparte para que ambos archivos usen exactamente el mismo
// corte y no se desincronicen.
export const DESKTOP_MEDIA_QUERY = "(min-width:900px)";

// Apply to the inner Box inside a <Toolbar disableGutters> that wraps a
// back-button + title. The Toolbar MUST have disableGutters, otherwise its
// own default gutter padding stacks on top of this Box's px and shifts the
// header to the right relative to the content container below it.
export const pageHeaderInnerSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  maxWidth: { xs: "100%", md: `${PAGE_CONTENT_MAX_WIDTH}px` },
  mx: "auto",
  px: 2,
  boxSizing: "border-box",
};

// Apply to the page's content wrapper. The wrapper MUST be a MUI <Container
// maxWidth={false} disableGutters> (or a plain <Box>) — a bare <Container>
// with no maxWidth/disableGutters applies its OWN default width/padding on
// top of this sx and reintroduces the mismatch.
export const pageContentSx = {
  width: "100%",
  maxWidth: { xs: "100%", md: `${PAGE_CONTENT_MAX_WIDTH}px` },
  mx: "auto",
  px: 2,
  boxSizing: "border-box",
};

// Panel de filtros sticky (desktop) en home/events/trips/cars: espacio a
// dejar abajo para la BottomToolbar (52px + el botón central que sobresale).
export const FILTER_PANEL_BOTTOM_GAP = 96;

// Si el panel no entra en la pantalla se puede scrollear (rueda/touch) pero
// sin barras visibles, y nunca en horizontal (algún control apenas más ancho
// que el panel generaba una barra X).
export const filterPanelScrollSx = {
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

// Sidebar de navegación de la versión web de escritorio (ver
// components/DesktopSidebar.js): reemplaza al AppBar de categorías y a la
// BottomToolbar desde DESKTOP_MEDIA_QUERY. Entre ese corte y
// SIDEBAR_WIDE_MEDIA_QUERY va angosta (solo íconos); de ahí para arriba,
// con textos. El ancho vigente se publica en la variable CSS --sidebar-w
// (ver App.js e index.css) para que los elementos position:fixed de cada
// pantalla arranquen a su derecha.
export const SIDEBAR_WIDE_MEDIA_QUERY = "(min-width:1280px)";
export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 76;

// Publicaciones del feed (ofertas/eventos) estilo Instagram en celular: de
// borde a borde (100vw, sale del px del contenedor), sin tarjeta (ni radio,
// ni bordes laterales, ni sombra) y la imagen pegada a los costados. Desde sm
// vuelven a ser tarjetas. Se aplica DESPUÉS de styles.listItem.
export const feedPostSx = {
  width: { xs: "100vw", sm: "100%" },
  ml: { xs: "calc(50% - 50vw)", sm: 0 },
  borderRadius: { xs: 0, sm: "8px" },
  borderLeft: { xs: "none" },
  borderRight: { xs: "none" },
  boxShadow: { xs: "none", sm: "0 1px 3px rgba(0,0,0,0.3)" },
  "&:hover": { boxShadow: { xs: "none", sm: "0 3px 8px rgba(0,0,0,0.4)" } },
  padding: { xs: 0, sm: 2 },
  mb: { xs: 1.5, sm: 4 },
};

// Cabecera (avatar + nombre) de la publicación en celular: sobre el mismo
// fondo que el post, sin línea abajo, como en Instagram.
export const feedPostHeaderSx = {
  padding: { xs: "10px 12px", sm: "12px 16px" },
  backgroundColor: { xs: "transparent" },
  borderBottom: { xs: "none" },
};

// Fila media + contenido: en celular la imagen arranca pegada a la cabecera.
export const feedPostBodySx = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  width: "100%",
  mt: { xs: 0, sm: 2 },
};

// Imagen/video de la publicación: sin bordes redondeados en celular.
export const feedPostMediaRadius = { xs: 0, sm: "6px" };

// Texto, botones y preguntas: el padding lateral que el post ya no tiene.
export const feedPostTextPx = { xs: 1.5, sm: 0 };

// Publicaciones de autos/propiedades (ver components/ListingPost.js): mismo
// formato que las del feed de home/events. En celular van de borde a borde,
// sin radio ni bordes laterales y con la foto arriba bien alta; en escritorio
// son una tarjeta por fila con la foto a la izquierda (30%) y los datos a la
// derecha, debajo de la cabecera con la cuenta.
export const listingCardSx = {
  display: "block",
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  overflow: "hidden",
  ...feedPostSx,
};
export const listingBodySx = feedPostBodySx;
export const listingMediaSx = {
  position: "relative",
  width: { xs: "100%", md: "30%" },
  flexShrink: 0,
};
export const listingContentSx = {
  flex: 1,
  minWidth: 0,
  px: { xs: 1.5, sm: 0 },
  pt: { xs: 1.5, sm: 2, md: 0 },
  pb: { xs: 1.5, sm: 0 },
  pl: { md: 2 },
};
export const listingImageHeight = { xs: "75vw", sm: 280, md: 180 };
export const listingImageRadius = { xs: 0, sm: "6px" };
